import React, { useState } from 'react'
import { useParams } from 'react-router-dom'

export default function NewsPage() {
  const { slug } = useParams<{ slug: string }>()
  const [selectedCategory, setSelectedCategory] = useState('all')

  const categories = [
    { id: 'all', name: 'All News', icon: '📰' },
    { id: 'forex', name: 'Forex', icon: '💱' },
    { id: 'stocks', name: 'Stocks', icon: '📊' },
    { id: 'crypto', name: 'Crypto', icon: '₿' },
    { id: 'analysis', name: 'Analysis', icon: '🔍' },
  ]

  // Mock news data - this will be replaced with real API calls
  const mockNews = [
    {
      id: 1,
      title: 'EUR/USD Breaks Key Resistance Level as ECB Signals Rate Pause',
      excerpt: 'The euro surged against the dollar following dovish comments from Federal Reserve officials, pushing the pair above the critical 1.0900 resistance level.',
      category: 'forex',
      author: 'Market Analysis Team',
      publishedAt: '2024-01-15T10:30:00Z',
      featuredImage: '/api/placeholder/400/200',
      readTime: '3 min read'
    },
    {
      id: 2,
      title: 'Tech Stocks Rally as AI Optimism Returns to Markets',
      excerpt: 'Major technology companies gained ground in early trading as investors renewed their focus on artificial intelligence developments and potential productivity gains.',
      category: 'stocks',
      author: 'Sarah Johnson',
      publishedAt: '2024-01-15T09:15:00Z',
      featuredImage: '/api/placeholder/400/200',
      readTime: '4 min read'
    },
    {
      id: 3,
      title: 'Bitcoin Consolidates Above $42K as Institutional Interest Grows',
      excerpt: 'Bitcoin price action remains stable above the $42,000 level as institutional investors continue to show increased interest in cryptocurrency allocations.',
      category: 'crypto',
      author: 'Crypto Desk',
      publishedAt: '2024-01-15T08:45:00Z',
      featuredImage: '/api/placeholder/400/200',
      readTime: '2 min read'
    },
    {
      id: 4,
      title: 'Weekly Market Outlook: Central Bank Decisions in Focus',
      excerpt: 'This week brings important central bank meetings from the Fed, ECB, and BoJ, with investors closely watching for signals about future monetary policy direction.',
      category: 'analysis',
      author: 'Global Markets Team',
      publishedAt: '2024-01-14T16:00:00Z',
      featuredImage: '/api/placeholder/400/200',
      readTime: '6 min read'
    },
    {
      id: 5,
      title: 'Oil Prices Surge on Middle East Supply Concerns',
      excerpt: 'Crude oil futures jumped 3% in overnight trading as geopolitical tensions in the Middle East raised concerns about potential supply disruptions.',
      category: 'stocks',
      author: 'Commodities Team',
      publishedAt: '2024-01-14T14:20:00Z',
      featuredImage: '/api/placeholder/400/200',
      readTime: '3 min read'
    }
  ]

  const filteredNews = selectedCategory === 'all'
    ? mockNews
    : mockNews.filter(article => article.category === selectedCategory)

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // If viewing a specific article
  if (slug) {
    const article = mockNews.find(a => a.title.toLowerCase().replace(/[^a-z0-9]+/g, '-') === slug)

    if (!article) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Article Not Found</h1>
            <a href="/news" className="btn btn-primary">Back to News</a>
          </div>
        </div>
      )
    }

    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Article Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-4">
              <a href="/news" className="hover:text-primary-600">News</a>
              <span>→</span>
              <span className="capitalize">{article.category}</span>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {article.title}
            </h1>

            <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-6">
              <span>By {article.author}</span>
              <span>•</span>
              <span>{formatDate(article.publishedAt)}</span>
              <span>•</span>
              <span>{article.readTime}</span>
            </div>

            <div className="w-full h-64 bg-muted rounded-lg flex items-center justify-center mb-8">
              <span className="text-muted-foreground">📸 Featured Image</span>
            </div>
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none dark:prose-invert">
            <p className="text-lg text-muted-foreground mb-6">
              {article.excerpt}
            </p>

            <p>
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
            </p>

            <p>
              Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
            </p>

            <h2>Market Implications</h2>

            <p>
              Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.
            </p>

            <h2>Looking Ahead</h2>

            <p>
              Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.
            </p>
          </div>

          {/* Article Footer */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm text-muted-foreground">Share:</span>
                <button className="text-muted-foreground hover:text-primary-600">Twitter</button>
                <button className="text-muted-foreground hover:text-primary-600">LinkedIn</button>
                <button className="text-muted-foreground hover:text-primary-600">Copy Link</button>
              </div>
              <a href="/news" className="btn btn-secondary btn-sm">
                ← Back to News
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // News listing page
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Market News & Analysis
          </h1>
          <p className="text-muted-foreground">
            Stay updated with the latest financial markets news and expert analysis
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`
                  flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                  ${selectedCategory === category.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }
                `}
              >
                <span>{category.icon}</span>
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* News Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredNews.map((article) => (
            <article key={article.id} className="card overflow-hidden hover:shadow-lg transition-shadow">
              <div className="w-full h-48 bg-muted flex items-center justify-center">
                <span className="text-muted-foreground">📸</span>
              </div>

              <div className="p-6">
                <div className="flex items-center space-x-2 mb-3">
                  <span className={`
                    px-2 py-1 text-xs font-medium rounded-full
                    ${article.category === 'forex' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                      article.category === 'stocks' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      article.category === 'crypto' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                      'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                    }
                  `}>
                    {categories.find(c => c.id === article.category)?.icon} {article.category.toUpperCase()}
                  </span>
                  <span className="text-xs text-muted-foreground">{article.readTime}</span>
                </div>

                <h2 className="text-lg font-semibold text-foreground mb-2 line-clamp-2">
                  {article.title}
                </h2>

                <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                  {article.excerpt}
                </p>

                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground">
                    <div>{article.author}</div>
                    <div>{formatDate(article.publishedAt)}</div>
                  </div>
                  <a
                    href={`/news/${article.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className="btn btn-secondary btn-sm"
                  >
                    Read More
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Load More */}
        {filteredNews.length > 0 && (
          <div className="text-center mt-12">
            <button className="btn btn-secondary">
              Load More Articles
            </button>
          </div>
        )}

        {/* Empty State */}
        {filteredNews.length === 0 && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">📰</div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No articles found
            </h3>
            <p className="text-muted-foreground">
              Try selecting a different category or check back later for new content.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}